import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { getSupplierUrl } from '@bridebook/toolbox/src';
import { Button } from '@bridebook/aura';
import { ArrowRight } from '@bridebook/aura/src/icons';
import { Box } from '@bridebook/ui';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { ReactComponent as VideoPlayButton } from '@bridebook/web/app-shared/components/icons/video-play-button.svg';
import ContentContainer from '@bridebook/web/app-basic/components/supplier/content-container';
import { FEATURES_SECTION_ID } from '@bridebook/web/app-shared/components/supplier/sections/ids';
import SupplierAccordion from '@bridebook/web/app-shared/components/supplier/sections/supplier-accordion/supplier-accordion';
import DataList from '@bridebook/web/app-basic/components/supplier/sections/features/data-list';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/features/features.style';
import Section from '@bridebook/web/app-shared/components/supplier/sections/features/section';
import SectionV2 from '@bridebook/web/app-shared/components/supplier/sections/features/section-v2';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { getIsVenue, getSupplier } from '@bridebook/web/app-basic/lib/supplier/selectors';
import { getSupplierVideoId, getSupplierVideoThumbnail } from '@bridebook/web/lib/supplier/video';
import { useSupplierAccordion } from '@bridebook/web/app-basic/components/supplier/hooks/use-supplier-accordion';
import { useFeatures } from './useFeatures';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';

export type TSupplierFeaturesComponentVersion = 'v1' | 'v2';

type TSectionComponent = typeof Section | typeof SectionV2;

const SECTION_COMPONENT_PER_VERSION: Record<TSupplierFeaturesComponentVersion, TSectionComponent> =
  {
    v1: Section,
    v2: SectionV2,
  };

const Features = () => {
  const { t } = useTranslation('supplier');
  const supplier = useSelector(getSupplier);
  const isVenue = useSelector(getIsVenue);
  const accordionProps = useSupplierAccordion();
  const isMobile = useSelector(getIsMobile);
  const supplierName = supplier?.name;

  const accordionText = useMemo(() => {
    if (isVenue) return null;
    return t('featuresAccordionLabel.name.details', { supplierName });
  }, [isVenue, supplierName, t]);

  const containerHeight = useMemo(() => {
    if (isVenue) return 0;
  }, [isVenue]);

  const features = useFeatures();

  const seoVideo = supplier?.seo?.video;
  const videoId = getSupplierVideoId(seoVideo);
  const hasProfileVideo = isVenue && Boolean(videoId);
  const videoHref = supplier ? `${getSupplierUrl(supplier)}/video` : '';
  const videoThumbnail = videoId ? getSupplierVideoThumbnail(seoVideo, videoId) : '';

  if (!features) return null;

  const styles = componentStyles();
  const version = isVenue ? 'v2' : 'v1';

  const SectionComponent = SECTION_COMPONENT_PER_VERSION[version];

  const { sections, typeDetails, culturalExperienceTags } = features;

  return (
    <Box id={FEATURES_SECTION_ID}>
      <SupplierAccordion
        {...accordionProps}
        openStatic
        name="features-accordion"
        horizontalMargins={isMobile}
        text={accordionText}
        containerHeight={containerHeight}>
        {isVenue && <DataList supplier={supplier as ISupplier} version={version} />}
        {sections.length > 0 && (
          <ContentContainer>
            <Box style={styles.sectionsWrapper}>
              {sections.map((key, index) => (
                <SectionComponent
                  key={`section-${key}`}
                  section={(key === 'tags' ? culturalExperienceTags : typeDetails[key]) as string[]}
                  name={key as string}
                  last={sections.length - 1 === index}
                  isMobile={isMobile}
                  details={typeDetails}
                />
              ))}
            </Box>
          </ContentContainer>
        )}
        {hasProfileVideo && (
          <ContentContainer>
            <Link
              href={videoHref}
              className="flex w-full flex-col items-stretch gap-4 rounded-2xl bg-indigo-crush-10 p-4 no-underline transition-colors hover:bg-indigo-crush-20 sm:flex-row sm:items-center sm:gap-5 sm:p-5">
              <div className="relative aspect-video w-full shrink-0 overflow-hidden rounded-xl bg-space-08 sm:w-[220px]">
                <img
                  src={videoThumbnail}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <VideoPlayButton aria-hidden />
                </div>
              </div>
              <div className="flex flex-1 flex-col items-start gap-2">
                <div className="text-lg font-semibold text-space">{t('video.cta.heading')}</div>
                <div className="text-space">
                  {t('video.cta.description', { name: supplierName })}
                </div>
                <Button
                  asChild
                  variant="secondary"
                  iconRight={ArrowRight}
                  className="mt-1 w-full sm:w-auto">
                  <span>{t('video.cta.button')}</span>
                </Button>
              </div>
            </Link>
          </ContentContainer>
        )}
      </SupplierAccordion>
    </Box>
  );
};

export default Features;
